import SaleSection from './sale-section';
import { Sale, SaleStatus } from '@/types/api/sale';
import { formatMoney } from '@/utils/shared/Utils';
import CircularProgressBar from './circular-progress-bar';
import SaleStatusDisplay from './sale-status-display';
import { SaleProfitBreakdown } from '@/features/sales/components/sale-profit-breakdown';
import { getNextStepText, getSaleSteps } from '@/utils/sale/steps';

interface SaleMainSectionProps extends Sale {
  /** The whole group's sale, for the profit breakdown and the steps on the meter. */
  groupSale?: Sale;
  /** Says whose profit it is, e.g. "Gruppens vinst" or "Din vinst". */
  profitLabel?: string;
}

export default function SaleMainSection({
  profit,
  status,
  cutoffAt,
  unitCount,
  unitCountTarget,
  groupSale,
  profitLabel = 'Vinst',
}: SaleMainSectionProps) {
  // Only an active sale can still reach a step.
  const nextStep = groupSale && status === SaleStatus.Active ? getNextStepText(groupSale) : null;

  return (
    <SaleSection>
      <div className="py-1">
        <div className="text-gray-500 dark:text-gray-400">{profitLabel}</div>
        <div className="text-4xl font-bold text-gray-800 dark:text-gray-100 mr-2">{formatMoney(profit)}</div>
        {groupSale && (
          <div className="mt-1">
            <SaleProfitBreakdown sale={groupSale} />
          </div>
        )}
      </div>

      <CircularProgressBar
        unitCount={unitCount}
        unitCountTarget={unitCountTarget}
        steps={groupSale ? getSaleSteps(groupSale) : undefined}
      />

      {nextStep && (
        <p className="-mt-4 pb-2 text-center text-sm text-gray-500 dark:text-gray-400">
          Nästa steg: <span className="font-medium text-gray-800 dark:text-gray-100">{nextStep}</span>
        </p>
      )}

      <InfoRow label="Status" value={<SaleStatusDisplay status={status} />} />
      <InfoRow label="Sista beställningsdag" value={new Date(cutoffAt).toLocaleDateString('sv-SE')} />
    </SaleSection>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center">
      <div className="text-gray-500 dark:text-gray-400">{label}</div>
      <div>{value}</div>
    </div>
  );
}
